import { ServiceHandler } from '../../../utils/api.utils';
import { getData } from '../../../utils/functional/fp';
import type { ChatContext } from '../types/context.types';

/**
 * Improvement framing, branched server-side (OMNI-443).
 * - `'chat'` (default): rewrite into a clearer chat message for the composer.
 * - `'task_instruction'`: refine into a standing automation directive for an
 *   agent — imperative, closed-loop, no follow-up questions (the task runs
 *   unattended, with no interactive session to answer them).
 */
export type ImprovePromptMode = 'chat' | 'task_instruction';

export interface ImprovePromptRequest {
  draft: string;
  agentId?: string;
  /** Same shape as chat stream — enriched server-side for grounded rewrites */
  contexts?: ChatContext[];
  /** Omitted ⇒ `'chat'` framing server-side. */
  mode?: ImprovePromptMode;
  /** Task skill — extra grounding context for `mode: 'task_instruction'` rewrites. */
  skillId?: string;
}

export interface ImprovePromptResponse {
  improvedPrompt: string;
}

const improvePromptRequest = ServiceHandler<
  ImprovePromptRequest,
  ImprovePromptResponse
>({
  url: '/api/chat-agent/v1/prompts/improve',
  method: 'POST',
});

export async function improvePrompt(
  body: ImprovePromptRequest
): Promise<ImprovePromptResponse> {
  const res = await improvePromptRequest({
    data: body,
    headers: {
      'X-Request-Id': crypto.randomUUID(),
    },
  });
  return getData(res) as ImprovePromptResponse;
}
